const HOST_SELECTOR = '[data-gs-biscuits-enhancements]';
const APP_FORM_SELECTOR = 'biscuits-bundle-form';
const PRODUCT_SELECTOR = 'biscuits-bundle-product[data-biscuits-handle]';
const NATIVE_BUTTON_SELECTOR = 'button[data-add-to-cart]';
const QUANTITY_INPUT_SELECTOR = '.biscuits-bundle-quantity__input';
const DECREASE_BUTTON_SELECTOR = 'button.biscuits-bundle-quantity__button--minus';
const INCREASE_BUTTON_SELECTOR = 'button.biscuits-bundle-quantity__button--plus';
const PRODUCT_JSON_SELECTOR = 'script.biscuits-bundle-item__json[type="application/json"]';

function numberFromText(value) {
  const match = String(value || '').match(/\d+/);
  return match ? Number.parseInt(match[0], 10) : 0;
}

function replaceTokens(template, values) {
  return Object.entries(values).reduce(
    (result, [key, value]) => result.replaceAll(`__${key}__`, String(value)),
    template
  );
}

function readProductIndex(host) {
  const indexElement = host.querySelector('[data-gs-biscuits-product-index]');
  if (!indexElement) return {};

  try {
    return JSON.parse(indexElement.textContent || '{}');
  } catch (error) {
    console.warn('[gs-biscuits] Product display data could not be read:', error);
    return {};
  }
}

function replaceAccessibleTitle(productElement, originalTitle, displayTitle) {
  if (!originalTitle || !displayTitle || originalTitle === displayTitle) return;

  productElement.querySelectorAll('[aria-label]').forEach((element) => {
    const label = element.getAttribute('aria-label');
    if (label?.includes(originalTitle)) {
      element.setAttribute('aria-label', label.replaceAll(originalTitle, displayTitle));
    }
  });
}

function selectedBiscuitsVariant(productElement) {
  const variantId = productElement.dataset.biscuitsSelectedVariantId;
  const productJson = productElement.querySelector(PRODUCT_JSON_SELECTOR);

  if (!variantId || !productJson) return null;

  try {
    const variants = JSON.parse(productJson.textContent || '[]');
    return variants.find((variant) => String(variant.variant_id) === String(variantId)) || null;
  } catch (error) {
    console.warn('[gs-biscuits] Biscuits inventory data could not be read:', error);
    return null;
  }
}

function inventoryLimitForProduct(productElement, productData) {
  const variantId = productElement.dataset.biscuitsSelectedVariantId;
  const shopifyVariant = productData?.variants?.[variantId];

  if (
    !shopifyVariant?.inventory_management ||
    shopifyVariant.inventory_policy !== 'deny' ||
    !Number.isFinite(Number(shopifyVariant.inventory_quantity))
  ) {
    return null;
  }

  const limits = [Math.max(Number(shopifyVariant.inventory_quantity), 0)];
  const biscuitsVariant = selectedBiscuitsVariant(productElement);
  const biscuitsQuantity = Number(biscuitsVariant?.quantity_available);

  if (Number.isFinite(biscuitsQuantity) && biscuitsQuantity >= 0) {
    limits.push(biscuitsQuantity);
  }

  return Math.min(...limits);
}

function updateDescribedBy(element, id, shouldInclude) {
  const ids = new Set((element.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean));

  if (shouldInclude) {
    ids.add(id);
  } else {
    ids.delete(id);
  }

  if (ids.size > 0) {
    element.setAttribute('aria-describedby', [...ids].join(' '));
  } else {
    element.removeAttribute('aria-describedby');
  }
}

function removeInventoryNotice(productElement) {
  const notice = productElement.querySelector('[data-gs-biscuits-stock-notice]');
  const increaseButton = productElement.querySelector(INCREASE_BUTTON_SELECTOR);

  if (notice && increaseButton) {
    updateDescribedBy(increaseButton, notice.id, false);
  }

  notice?.remove();
  delete productElement.dataset.gsBiscuitsStockLimit;
  delete productElement.dataset.gsBiscuitsInventoryExceeds;
  delete productElement.dataset.gsBiscuitsInventoryMessage;
}

function applyProductInventoryLimit(productElement, productData, bundleTotal) {
  const quantityInput = productElement.querySelector(QUANTITY_INPUT_SELECTOR);
  const increaseButton = productElement.querySelector(INCREASE_BUTTON_SELECTOR);
  const quantityContainer = productElement.querySelector('.biscuits-bundle-quantity');
  const limit = inventoryLimitForProduct(productElement, productData);

  if (
    !(quantityInput instanceof HTMLInputElement) ||
    !(increaseButton instanceof HTMLButtonElement) ||
    !quantityContainer ||
    limit === null
  ) {
    removeInventoryNotice(productElement);
    return;
  }

  const quantity = Number.parseInt(quantityInput.value || '0', 10) || 0;
  const exceedsLimit = quantity > limit;
  const atLimit = limit > 0 && quantity >= limit;
  const shouldShowNotice = bundleTotal > 0 && limit < bundleTotal;
  const productTitle =
    productData?.title ||
    productElement.querySelector('.biscuits-bundle-item__product-title')?.textContent.trim() ||
    'this item';

  productElement.dataset.gsBiscuitsStockLimit = String(limit);
  productElement.dataset.gsBiscuitsInventoryExceeds = String(exceedsLimit);
  productElement.dataset.gsBiscuitsInventoryMessage = exceedsLimit
    ? `Only ${limit} available for ${productTitle}. Reduce its quantity before adding.`
    : '';

  let notice = productElement.querySelector('[data-gs-biscuits-stock-notice]');

  if (!shouldShowNotice) {
    if (notice) {
      updateDescribedBy(increaseButton, notice.id, false);
      notice.remove();
    }
    return;
  }

  const noticeId = `GsBiscuitsStock-${productElement.dataset.biscuitsHandle || 'item'}-${productElement.dataset.biscuitsSelectedVariantId || 'variant'}`
    .replace(/[^a-zA-Z0-9_-]/g, '-');

  if (!notice) {
    notice = document.createElement('p');
    notice.className = 'gs-biscuits-stock-note';
    notice.dataset.gsBiscuitsStockNotice = 'true';
    quantityContainer.insertAdjacentElement('afterend', notice);
  } else if (notice.id && notice.id !== noticeId) {
    updateDescribedBy(increaseButton, notice.id, false);
  }

  notice.id = noticeId;
  notice.dataset.atLimit = String(atLimit);
  notice.dataset.exceedsLimit = String(exceedsLimit);

  let noticeText = `Only ${limit} available for this bundle.`;

  if (exceedsLimit) {
    noticeText = `Only ${limit} available. Reduce this item to ${limit} before adding.`;
  } else if (atLimit) {
    noticeText = `Maximum selected — only ${limit} available for this bundle.`;
  }

  if (notice.textContent !== noticeText) {
    notice.textContent = noticeText;
  }

  if (atLimit || exceedsLimit) {
    notice.setAttribute('role', 'status');
    notice.setAttribute('aria-live', 'polite');
  } else {
    notice.removeAttribute('role');
    notice.removeAttribute('aria-live');
  }

  updateDescribedBy(increaseButton, notice.id, true);
}

function applyProductDisplay(productElement, productData, options) {
  const card = productElement.closest('.biscuits-bundle-item-card');
  const unavailableInBiscuits =
    productElement.dataset.biscuitsAvailable === 'false' || card?.dataset.biscuitsAvailable === 'false';
  const unavailableInShopify = productData?.available === false;
  const shouldHide = options.hideUnavailable && (unavailableInBiscuits || unavailableInShopify);

  if (card) {
    if (shouldHide) {
      card.hidden = true;
      card.dataset.gsBiscuitsHiddenUnavailable = 'true';
    } else if (card.dataset.gsBiscuitsHiddenUnavailable === 'true') {
      card.hidden = false;
      delete card.dataset.gsBiscuitsHiddenUnavailable;
    }
  }

  if (!productData) return;

  const titleElement = productElement.querySelector('.biscuits-bundle-item__product-title');
  const checkboxLabel = productElement.querySelector('.biscuits-bundle-item-checkbox__label');
  const originalTitle =
    productElement.dataset.gsBiscuitsOriginalTitle || titleElement?.textContent.trim() || checkboxLabel?.textContent.trim();

  if (originalTitle) {
    productElement.dataset.gsBiscuitsOriginalTitle = originalTitle;
  }

  if (options.useShortTitles && productData.title) {
    if (titleElement && titleElement.textContent.trim() !== productData.title) {
      titleElement.textContent = productData.title;
    }

    if (checkboxLabel && checkboxLabel.textContent.trim() !== productData.title) {
      checkboxLabel.textContent = productData.title;
    }

    replaceAccessibleTitle(productElement, originalTitle, productData.title);
  }

  if (!options.useCutouts || !productData.image?.src) return;

  productElement.querySelectorAll('.biscuits-bundle-item__image img').forEach((image) => {
    if (image.getAttribute('src') !== productData.image.src) {
      image.setAttribute('src', productData.image.src);
    }

    if (productData.image.srcset && image.getAttribute('srcset') !== productData.image.srcset) {
      image.setAttribute('srcset', productData.image.srcset);
    }

    if (productData.image.alt) {
      image.setAttribute('alt', productData.image.alt);
    }

    if (productData.image.width) {
      image.setAttribute('width', productData.image.width);
    }

    if (productData.image.height) {
      image.setAttribute('height', productData.image.height);
    }

    image.dataset.gsBiscuitsCutout = 'true';
  });
}

function normalizedText(value) {
  return String(value || '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim();
}

function productText(productElement, productData) {
  return normalizedText([
    productElement.dataset.biscuitsHandle,
    productElement.dataset.gsBiscuitsOriginalTitle,
    productData?.title,
    productData?.vendor,
    productData?.type,
  ].filter(Boolean).join(' '));
}

function rankFromTerms(text, terms, fallback = terms.length + 1) {
  const index = terms.findIndex((term) => text.includes(term));
  return index >= 0 ? index : fallback;
}

function productBrand(text) {
  const brands = [
    'skittles',
    'starburst',
    'sour patch',
    'm and ms',
    'm and m',
    'mms',
    'm m',
    'mentos',
    'mike and ike',
    'hi chew',
    'creme savers',
    'snickers',
    'twix',
    'oreo',
    'chips ahoy',
    'hershey',
    'pop tarts',
    'walkers',
    'monster munch',
    'lays',
    'cheetos',
    'doritos',
    'pringles',
    'fanta',
    'pepsi',
    'sprite',
    'coca cola',
    'dr pepper',
    'gatorade',
  ];

  const aliases = {
    'm and ms': 'mms',
    'm and m': 'mms',
    'm m': 'mms',
  };

  const brand = brands.find((term) => text.includes(term)) || '';
  return aliases[brand] || brand || text;
}

function grabBagTypeRank(text, productData) {
  const productType = normalizedText(productData?.type);

  if (/\b(chip|chips|crisps)\b/.test(productType)) return 10;
  if (/\b(cookie|cookies|wafer|biscuit|biscuits|cake|popcorn)\b/.test(productType)) return 20;
  if (/\b(drink|drinks|beverage|beverages|soda)\b/.test(productType)) return 30;
  if (/\b(candy|chocolate|gummies|gummy|hard candy)\b/.test(productType)) return 40;

  if (isMerchProduct(text, productData)) return 90;
  if (/\b(chip|chips|cheetos|doritos|pringles|walkers|monster munch|lays|calbee)\b/.test(text)) {
    return 10;
  }
  if (/\b(cookie|cookies|oreo|wafer|biscuit|biscuits|pop tart|pop tarts|cake|waffle)\b/.test(text)) {
    return 20;
  }
  if (/\b(drink|fanta|pepsi|sprite|coca cola|dr pepper|gatorade)\b/.test(text)) return 30;
  return 40;
}

function isMerchProduct(text, productData) {
  const productType = normalizedText(productData?.type);
  return /\b(merch|apparel|shirt|shirts|t shirt|tshirt|sock|socks|clip|clips)\b/.test(`${productType} ${text}`);
}

function sortKeyForProduct(productElement, productData, mode, originalIndex) {
  const text = productText(productElement, productData);
  const brand = productBrand(text);
  const brandRank420 = rankFromTerms(text, [
    'skittles',
    'snickers',
    'twix',
    'oreo',
    'chips ahoy',
    'hershey',
    'sour patch',
    'mike and ike',
    'hi chew',
    'starburst',
    'mms',
    'm and ms',
    'mentos',
    'fanta',
    'pepsi',
    'sprite',
    'coca cola',
    'dr pepper',
    'gatorade',
    'chip bag clip',
    'clip',
    'merch',
  ]);

  if (mode === '420') {
    const merchRank = isMerchProduct(text, productData) ? 99 : 0;
    return [merchRank, brandRank420, brand, text, originalIndex];
  }

  if (mode === 'grab_bag') {
    const brandRankGrabBag = rankFromTerms(text, [
      'walkers',
      'monster munch',
      'lays',
      'cheetos',
      'doritos',
      'pringles',
      'oreo',
      'chips ahoy',
      'pop tarts',
      'hershey',
      'skittles',
      'starburst',
      'm and ms',
      'm and m',
      'mms',
      'm m',
      'mentos',
      'sour patch',
      'mike and ike',
      'hi chew',
      'creme savers',
      'snickers',
      'twix',
      'clip',
      'merch',
    ]);

    return [grabBagTypeRank(text, productData), brandRankGrabBag, brand, text, originalIndex];
  }

  return [originalIndex];
}

function compareSortKeys(first, second) {
  const length = Math.max(first.length, second.length);

  for (let index = 0; index < length; index += 1) {
    const firstValue = first[index];
    const secondValue = second[index];

    if (firstValue === secondValue) continue;
    if (typeof firstValue === 'number' && typeof secondValue === 'number') {
      return firstValue - secondValue;
    }

    return String(firstValue).localeCompare(String(secondValue), undefined, {
      numeric: true,
      sensitivity: 'base',
    });
  }

  return 0;
}

function applyPickerDisplayOrder(appForm, productIndex, mode) {
  if (!['420', 'grab_bag'].includes(mode)) return;

  const products = [...appForm.querySelectorAll(PRODUCT_SELECTOR)];
  const sortableCards = products
    .map((productElement, index) => {
      const card = productElement.closest('.biscuits-bundle-item-card');
      const parent = card?.parentElement;
      const handle = productElement.dataset.biscuitsHandle;

      if (!card || !parent) return null;

      return {
        card,
        handle,
        parent,
        originalIndex: index,
        sortKey: sortKeyForProduct(productElement, productIndex[handle], mode, index),
      };
    })
    .filter(Boolean);

  if (sortableCards.length < 2) return;

  const parentGroups = new Map();
  sortableCards.forEach((entry) => {
    if (!parentGroups.has(entry.parent)) {
      parentGroups.set(entry.parent, []);
    }
    parentGroups.get(entry.parent).push(entry);
  });

  parentGroups.forEach((entries, parent) => {
    const sorted = [...entries].sort((first, second) => compareSortKeys(first.sortKey, second.sortKey));
    const nextSignature = sorted.map((entry) => entry.handle || entry.card.textContent.trim()).join('|');

    if (parent.dataset.gsBiscuitsDisplayOrderSignature === nextSignature) return;

    sorted.forEach((entry) => parent.appendChild(entry.card));
    parent.dataset.gsBiscuitsDisplayOrderSignature = nextSignature;
    parent.dataset.gsBiscuitsDisplayOrderMode = mode;
  });
}

function selectionState(appForm) {
  const quantityElement =
    appForm.querySelector('[data-biscuits-step-quantity]') ||
    appForm.querySelector('[data-biscuits-step-selected]');
  const counter = quantityElement?.closest('.biscuits-bundle-step__counter');
  const maximumElement = counter?.querySelector('.biscuits-bundle-step__max');

  return {
    selected: numberFromText(quantityElement?.textContent),
    total: numberFromText(maximumElement?.textContent),
  };
}

function buttonIsLoading(button) {
  return (
    button.dataset.biscuitsLoading === 'true' ||
    button.getAttribute('aria-busy') === 'true' ||
    button.classList.contains('loading') ||
    button.classList.contains('biscuits--loading')
  );
}

function clearStickyOutcome(stickyBar) {
  delete stickyBar.dataset.postAdd;
  delete stickyBar.dataset.addError;
  delete stickyBar.dataset.addErrorMessage;
  delete stickyBar.dataset.justReset;
}

function inventoryIssue(appForm) {
  const productElement = appForm.querySelector(
    `${PRODUCT_SELECTOR}[data-gs-biscuits-inventory-exceeds="true"]`
  );

  if (!productElement) return null;

  return {
    message:
      productElement.dataset.gsBiscuitsInventoryMessage ||
      'Reduce the over-limit item before adding this bundle.',
    productElement,
  };
}

function updateStickyBar(host, appForm, nativeButton) {
  const stickyBar = host.querySelector('[data-gs-biscuits-sticky]');
  const stickyButton = stickyBar?.querySelector('[data-gs-biscuits-sticky-button]');
  const stickyStatus = stickyBar?.querySelector('[data-gs-biscuits-sticky-status]');
  const stickyLabel = stickyBar?.querySelector('[data-gs-biscuits-sticky-label]');
  const stickyPrice = stickyBar?.querySelector('[data-gs-biscuits-sticky-price]');
  const stickyActions = stickyBar?.querySelector('[data-gs-biscuits-sticky-actions]');
  const buildAnotherButton = stickyBar?.querySelector('[data-gs-biscuits-build-another]');

  if (
    !stickyBar ||
    !stickyButton ||
    !stickyStatus ||
    !stickyLabel ||
    !stickyPrice ||
    !stickyActions ||
    !buildAnotherButton
  ) {
    return;
  }

  const { selected, total } = selectionState(appForm);
  const remaining = Math.max(total - selected, 0);
  const complete = total > 0 && remaining === 0;
  const loading = buttonIsLoading(nativeButton);
  const postAdd = stickyBar.dataset.postAdd === 'true';
  const addError = stickyBar.dataset.addError === 'true';
  const justReset = stickyBar.dataset.justReset === 'true';
  const stockIssue = inventoryIssue(appForm);
  const nativeEnabled = !nativeButton.disabled && !nativeButton.classList.contains('biscuits--disabled');
  const totalPrice = nativeButton.querySelector('[data-biscuits-form-total]')?.textContent.trim() || '';

  let selectionText = replaceTokens(stickyBar.dataset.selectionTemplate || '', {
    selected,
    total,
  });

  if (postAdd) {
    selectionText = stickyBar.dataset.addedStatus || selectionText;
  } else if (stockIssue) {
    selectionText = stockIssue.message;
  } else if (addError) {
    selectionText =
      stickyBar.dataset.addErrorMessage ||
      stickyBar.dataset.errorStatus ||
      selectionText;
  } else if (justReset) {
    selectionText = replaceTokens(stickyBar.dataset.resetStatusTemplate || '', {
      selected,
      total,
    });
  }

  if (stickyStatus.textContent !== selectionText) {
    stickyStatus.textContent = selectionText;
  }

  let buttonText = stickyBar.dataset.chooseItemsLabel || '';
  if (loading) {
    buttonText = stickyBar.dataset.loadingLabel || buttonText;
  } else if (stockIssue) {
    buttonText = stickyBar.dataset.adjustQuantityLabel || buttonText;
  } else if (complete && nativeEnabled) {
    buttonText = stickyBar.dataset.addBundleLabel || buttonText;
  } else if (remaining > 0) {
    buttonText = replaceTokens(stickyBar.dataset.chooseMoreTemplate || '', {
      count: remaining,
    });
  }

  stickyLabel.textContent = buttonText;
  stickyPrice.textContent = totalPrice ? ` • ${totalPrice}` : '';
  stickyPrice.hidden = !totalPrice;
  stickyButton.hidden = postAdd;
  stickyActions.hidden = !postAdd;
  stickyButton.disabled = postAdd || Boolean(stockIssue) || !complete || !nativeEnabled || loading;
  stickyButton.setAttribute('aria-disabled', String(stickyButton.disabled));
  stickyButton.setAttribute('aria-busy', String(loading));
  buildAnotherButton.setAttribute(
    'aria-busy',
    String(buildAnotherButton.dataset.gsBiscuitsResetting === 'true')
  );
  stickyBar.dataset.complete = String(complete && nativeEnabled && !stockIssue);
  stickyBar.dataset.ready = String(total > 0);
}

function setUpStickyVisibility(appForm, nativeButton, stickyBar) {
  let formIsVisible = false;
  let nativeButtonIsVisible = false;

  if (!('IntersectionObserver' in window)) {
    stickyBar.hidden = false;
    return;
  }

  const renderVisibility = () => {
    const postAdd = stickyBar.dataset.postAdd === 'true';
    stickyBar.hidden = !formIsVisible || (nativeButtonIsVisible && !postAdd);
  };

  const formObserver = new IntersectionObserver(
    ([entry]) => {
      formIsVisible = entry.isIntersecting;
      renderVisibility();
    },
    { threshold: 0 }
  );

  const buttonObserver = new IntersectionObserver(
    ([entry]) => {
      nativeButtonIsVisible = entry.isIntersecting && entry.intersectionRatio >= 0.5;
      renderVisibility();
    },
    { threshold: [0, 0.5, 1] }
  );

  formObserver.observe(appForm);
  buttonObserver.observe(nativeButton);

  const stateObserver = new MutationObserver(renderVisibility);
  stateObserver.observe(stickyBar, {
    attributes: true,
    attributeFilter: ['data-post-add'],
  });
}

function nextAnimationFrame() {
  return new Promise((resolve) => window.requestAnimationFrame(resolve));
}

async function closeOpenCartDrawer() {
  const cartDrawer = document.querySelector('theme-drawer#cart-drawer[open]');
  if (!(cartDrawer instanceof HTMLElement)) return;

  await customElements.whenDefined('theme-drawer');

  const drawerClosed = new Promise((resolve) => {
    let settled = false;
    const settle = () => {
      if (settled) return;
      settled = true;
      window.clearTimeout(timeout);
      resolve();
    };
    const timeout = window.setTimeout(settle, 500);

    cartDrawer.addEventListener('theme-drawer:close', settle, { once: true });

    if (typeof cartDrawer.close === 'function') {
      cartDrawer.close();
      return;
    }

    cartDrawer.querySelector('.theme-drawer__close-button')?.click();
  });

  await drawerClosed;
  await nextAnimationFrame();
}

async function resetBundleForm(appForm) {
  const maximumSelections = Math.max(selectionState(appForm).total, 1);
  let attemptsRemaining = maximumSelections * 2;

  while (attemptsRemaining > 0) {
    const selectedInput = [...appForm.querySelectorAll(QUANTITY_INPUT_SELECTOR)].find(
      (input) => Number.parseInt(input.value || '0', 10) > 0
    );

    if (!selectedInput) break;

    const productElement = selectedInput.closest(PRODUCT_SELECTOR);
    const decreaseButton = productElement?.querySelector(DECREASE_BUTTON_SELECTOR);

    if (
      !(decreaseButton instanceof HTMLButtonElement) ||
      decreaseButton.disabled ||
      decreaseButton.classList.contains('biscuits--disabled')
    ) {
      throw new Error('A selected Biscuits item could not be reset.');
    }

    decreaseButton.click();
    attemptsRemaining -= 1;
    await nextAnimationFrame();
  }

  await nextAnimationFrame();

  if (selectionState(appForm).selected !== 0) {
    throw new Error('The Biscuits bundle selection did not fully reset.');
  }
}

function focusFirstBundleChoice(appForm) {
  const firstAvailableProduct = [...appForm.querySelectorAll(PRODUCT_SELECTOR)].find((productElement) => {
    const card = productElement.closest('.biscuits-bundle-item-card');
    return productElement.dataset.biscuitsAvailable !== 'false' && !card?.hidden;
  });
  const firstIncreaseButton = firstAvailableProduct?.querySelector(
    'button.biscuits-bundle-quantity__button--plus'
  );

  firstAvailableProduct?.scrollIntoView({ behavior: 'auto', block: 'start' });

  if (firstIncreaseButton instanceof HTMLButtonElement) {
    firstIncreaseButton.focus({ preventScroll: true });
  }
}

async function submitBundleForm(nativeButton) {
  const nativeForm = nativeButton.form || nativeButton.closest('form');
  if (!nativeForm) throw new Error('Biscuits bundle form was not available.');

  const itemsByIndex = new Map();
  const formData = new FormData(nativeForm);

  for (const [name, value] of formData.entries()) {
    const itemMatch = name.match(/^items\[(\d+)\]\[(id|quantity|selling_plan)\]$/);
    const propertyMatch = name.match(/^items\[(\d+)\]\[properties\]\[(.+)\]$/);

    if (itemMatch) {
      const index = Number.parseInt(itemMatch[1], 10);
      const item = itemsByIndex.get(index) || { properties: {} };
      item[itemMatch[2]] = value;
      itemsByIndex.set(index, item);
    } else if (propertyMatch) {
      const index = Number.parseInt(propertyMatch[1], 10);
      const item = itemsByIndex.get(index) || { properties: {} };
      item.properties[propertyMatch[2]] = value;
      itemsByIndex.set(index, item);
    }
  }

  const items = [...itemsByIndex.entries()]
    .sort(([firstIndex], [secondIndex]) => firstIndex - secondIndex)
    .map(([, item]) => item);

  if (items.length === 0 || items.some((item) => !item.id)) {
    throw new Error('Biscuits bundle line items were not available.');
  }

  const endpoint = nativeForm.action.replace(/\/cart\/add\/?$/, '/cart/add.js');
  const response = await fetch(endpoint, {
    method: 'POST',
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ items }),
  });

  if (!response.ok) {
    let responseData = null;

    try {
      responseData = await response.json();
    } catch {
      responseData = null;
    }

    const responseMessage = String(responseData?.description || responseData?.message || '');
    const error = new Error(`Biscuits bundle could not be added: ${response.status}`);
    error.customerMessage =
      /available|inventory|maximum|sold out|stock|can't add|cannot add/i.test(responseMessage)
        ? 'One of those snacks no longer has enough stock. Reduce its quantity and try again.'
        : 'Bundle could not be added. Please try again.';
    throw error;
  }
}

function initializeAppForm(host, appForm) {
  if (appForm.dataset.gsBiscuitsEnhancementsReady === 'true') return;

  const nativeButton = appForm.querySelector(NATIVE_BUTTON_SELECTOR);
  if (!nativeButton) return;

  appForm.dataset.gsBiscuitsEnhancementsReady = 'true';

  const productIndex = readProductIndex(host);
  const productDataUrlTemplate = host.dataset.gsProductDataUrlTemplate || '';
  const pendingProductData = new Set();
  const options = {
    hideUnavailable: host.dataset.gsHideUnavailable === 'true',
    pickerSortMode: host.dataset.gsPickerSortMode || 'biscuits',
    useCutouts: host.dataset.gsUseCutouts === 'true',
    useShortTitles: host.dataset.gsUseShortTitles === 'true',
  };
  const stickyBar = host.querySelector('[data-gs-biscuits-sticky]');
  const stickyButton = stickyBar?.querySelector('[data-gs-biscuits-sticky-button]');
  const buildAnotherButton = stickyBar?.querySelector('[data-gs-biscuits-build-another]');
  const buildAnotherLabel = stickyBar?.querySelector('[data-gs-biscuits-build-another-label]');
  const buildAnotherText = buildAnotherLabel?.textContent.trim() || '';
  let updateQueued = false;
  let resettingBundle = false;

  const requestMissingProductData = async (handle) => {
    if (!handle || !productDataUrlTemplate || pendingProductData.has(handle)) return;
    if (Object.keys(productIndex).length === 0) return;

    pendingProductData.add(handle);

    try {
      const response = await fetch(productDataUrlTemplate.replace('__handle__', encodeURIComponent(handle)), {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });

      if (!response.ok) throw new Error(`Product display request failed: ${response.status}`);

      const productData = await response.json();
      if (productData?.handle === handle) {
        productData.source = 'fallback';
        productIndex[handle] = productData;
        host.dataset.gsBiscuitsFallbackLoaded = 'true';
        queueUpdate();
      }
    } catch (error) {
      console.warn(`[gs-biscuits] Display data was unavailable for ${handle}:`, error);
    }
  };

  const update = () => {
    updateQueued = false;
    const { total } = selectionState(appForm);

    appForm.querySelectorAll(PRODUCT_SELECTOR).forEach((productElement) => {
      const handle = productElement.dataset.biscuitsHandle;
      const productData = productIndex[handle];
      productElement.dataset.gsBiscuitsDataSource = productData?.source || (productData ? 'collection' : 'fallback');
      applyProductDisplay(productElement, productData, options);
      applyProductInventoryLimit(productElement, productData, total);

      if (!productData) {
        void requestMissingProductData(handle);
      }
    });

    applyPickerDisplayOrder(appForm, productIndex, options.pickerSortMode);

    appForm.dataset.gsBiscuitsInventoryIssue = String(Boolean(inventoryIssue(appForm)));

    if (stickyBar) {
      updateStickyBar(host, appForm, nativeButton);
    }
  };

  const queueUpdate = () => {
    if (updateQueued) return;
    updateQueued = true;
    window.requestAnimationFrame(update);
  };

  const observer = new MutationObserver(queueUpdate);
  observer.observe(appForm, {
    attributes: true,
    attributeFilter: [
      'aria-busy',
      'aria-checked',
      'class',
      'data-biscuits-available',
      'data-biscuits-loading',
      'data-biscuits-success',
      'disabled',
      'src',
    ],
    characterData: true,
    childList: true,
    subtree: true,
  });

  const handleBuilderChange = () => {
    if (stickyBar && !resettingBundle) {
      clearStickyOutcome(stickyBar);
    }
    queueUpdate();
  };

  appForm.addEventListener('change', handleBuilderChange);
  appForm.addEventListener('input', handleBuilderChange);
  appForm.addEventListener(
    'click',
    (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;

      const increaseButton = target.closest(INCREASE_BUTTON_SELECTOR);
      const productElement = increaseButton?.closest(PRODUCT_SELECTOR);

      if (increaseButton instanceof HTMLButtonElement && productElement) {
        const quantityInput = productElement.querySelector(QUANTITY_INPUT_SELECTOR);
        const limit = Number(productElement.dataset.gsBiscuitsStockLimit);
        const quantity =
          quantityInput instanceof HTMLInputElement
            ? Number.parseInt(quantityInput.value || '0', 10) || 0
            : 0;

        if (Number.isFinite(limit) && quantity >= limit) {
          event.preventDefault();
          event.stopImmediatePropagation();
          queueUpdate();
          return;
        }
      }

      const addButton = target.closest(NATIVE_BUTTON_SELECTOR);
      const stockIssue = inventoryIssue(appForm);

      if (addButton instanceof HTMLButtonElement && stockIssue) {
        event.preventDefault();
        event.stopImmediatePropagation();
        stockIssue.productElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        queueUpdate();
      }
    },
    { capture: true }
  );

  appForm.addEventListener('gs:biscuits-cart-settled', (event) => {
    if (!stickyBar) return;

    delete stickyBar.dataset.justReset;
    stickyBar.dataset.postAdd = event.detail?.succeeded ? 'true' : 'false';
    stickyBar.dataset.addError = event.detail?.succeeded ? 'false' : 'true';
    queueUpdate();
  });

  if (stickyBar && stickyButton) {
    stickyButton.addEventListener('click', async (event) => {
      event.preventDefault();
      if (stickyButton.disabled || nativeButton.disabled) return;
      if (inventoryIssue(appForm)) {
        queueUpdate();
        return;
      }

      clearStickyOutcome(stickyBar);

      try {
        await submitBundleForm(nativeButton);
      } catch (error) {
        nativeButton.dataset.biscuitsLoading = 'false';
        nativeButton.dataset.biscuitsSuccess = 'false';
        stickyBar.dataset.addError = 'true';
        stickyBar.dataset.addErrorMessage =
          error.customerMessage || stickyBar.dataset.errorStatus || '';
        console.warn('[gs-biscuits] Bundle add-to-cart failed:', error);
      } finally {
        queueUpdate();
      }
    });

    buildAnotherButton?.addEventListener('click', async () => {
      if (resettingBundle) return;

      resettingBundle = true;
      buildAnotherButton.disabled = true;
      buildAnotherButton.dataset.gsBiscuitsResetting = 'true';

      if (buildAnotherLabel) {
        buildAnotherLabel.textContent = stickyBar.dataset.resettingLabel || buildAnotherText;
      }

      try {
        await closeOpenCartDrawer();
        await resetBundleForm(appForm);
        nativeButton.dataset.biscuitsLoading = 'false';
        nativeButton.dataset.biscuitsSuccess = 'false';
        clearStickyOutcome(stickyBar);
        stickyBar.dataset.justReset = 'true';
        focusFirstBundleChoice(appForm);
      } catch (error) {
        clearStickyOutcome(stickyBar);
        stickyBar.dataset.addError = 'true';
        console.warn('[gs-biscuits] Bundle reset failed:', error);
      } finally {
        resettingBundle = false;
        buildAnotherButton.disabled = false;
        delete buildAnotherButton.dataset.gsBiscuitsResetting;

        if (buildAnotherLabel) {
          buildAnotherLabel.textContent = buildAnotherText;
        }

        queueUpdate();
      }
    });

    setUpStickyVisibility(appForm, nativeButton, stickyBar);
  }

  update();
}

function initializeHost(host) {
  if (host.dataset.gsBiscuitsHostReady === 'true') return;
  host.dataset.gsBiscuitsHostReady = 'true';

  const initialize = () => {
    const appForm = host.querySelector(APP_FORM_SELECTOR);
    if (!appForm) return false;
    initializeAppForm(host, appForm);
    return appForm.dataset.gsBiscuitsEnhancementsReady === 'true';
  };

  if (initialize()) return;

  const observer = new MutationObserver(() => {
    if (initialize()) observer.disconnect();
  });

  observer.observe(host, { childList: true, subtree: true });
}

function initializeAllHosts(root = document) {
  root.querySelectorAll(HOST_SELECTOR).forEach(initializeHost);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => initializeAllHosts(), { once: true });
} else {
  initializeAllHosts();
}

document.addEventListener('shopify:section:load', (event) => {
  initializeAllHosts(event.target);
});
